import { DEFAULT_PICTURE_SECONDS } from '../layout/playlist'
import type { MediaItem, MediaKind } from '../layout/types'
import type { PlaylistEntry, PlaylistSummary, SignageMedia } from './api'
import { entryFor, MAX_ENTRIES, MAX_SECONDS, MIN_SECONDS, moveEntry, totalSeconds } from './signageEdit'
import type { StaffKey } from './staffText'

// The playlist cards and editor: what they show, worked out without the browser.

export type Said = { key: StaffKey; n?: number }

/** An item being edited: `typed` holds a seconds box the server would refuse, until it is fixed. */
export type DraftEntry = PlaylistEntry & { typed?: string }

/** What the strip says under an item: "end" for a film to its end, its seconds, or a refused time as typed. */
export function stripSeconds(e: DraftEntry, m: Pick<SignageMedia, 'kind'> | undefined): Said | { raw: string } {
  if (!secondsValid(e.seconds, m?.kind ?? 'image')) return { raw: e.typed?.trim() || (e.seconds >= 0 ? String(e.seconds) : '—') }
  if (e.seconds > 0) return { key: 'signage.secs', n: e.seconds }
  return m?.kind === 'video' ? { key: 'signage.toEnd' } : { key: 'signage.secs', n: DEFAULT_PICTURE_SECONDS }
}

const KEY_STEP: Record<string, number> = { ArrowLeft: -1, ArrowUp: -1, ArrowRight: 1, ArrowDown: 1 }

/** The selected item moved one place by an arrow key; null when the key does nothing. */
export function moveByKey(entries: readonly PlaylistEntry[], index: number, key: string): { entries: PlaylistEntry[]; index: number } | null {
  const step = KEY_STEP[key]
  if (!step) return null
  const to = index + step
  if (index < 0 || to < 0 || to >= entries.length) return null
  return { entries: moveEntry([...entries], index, to), index: to }
}

/** The selection after removing an item from a list of `length`: it stays on the same place, or the new last, or none. */
export function afterRemove(length: number, removed: number, selected: number): number {
  if (length <= 1) return -1
  if (selected > removed) return selected - 1
  return Math.min(selected, length - 2)
}

/** Library files added at the end, as many as fit under the fifty-item limit. */
export function addEntries(entries: readonly PlaylistEntry[], chosen: readonly SignageMedia[]): PlaylistEntry[] {
  return [...entries, ...chosen.map(entryFor)].slice(0, Math.max(MAX_ENTRIES, entries.length))
}

/** A seconds box as typed: empty is the default, a whole number is itself (in range or not), anything else -1. */
export function secondsInput(raw: string, kind: MediaKind): number {
  const s = raw.trim()
  if (s === '') return kind === 'video' ? 0 : DEFAULT_PICTURE_SECONDS
  return /^\d+$/.test(s) ? Number(s) : -1
}

/** What the server takes: 3–3600, or 0 for a film to its end (internal/signage). */
export function secondsValid(seconds: number, kind: MediaKind): boolean {
  if (seconds === 0) return kind === 'video'
  return Number.isInteger(seconds) && seconds >= MIN_SECONDS && seconds <= MAX_SECONDS
}

/** An item after its seconds box changed; an emptied film box is refused, as "to end" is the menu's choice. */
export function typedEntry(e: DraftEntry, raw: string, kind: MediaKind): DraftEntry {
  const seconds = kind === 'video' && raw.trim() === '' ? -1 : secondsInput(raw, kind)
  const { typed: _, ...rest } = e
  return secondsValid(seconds, kind) ? { ...rest, seconds } : { ...rest, seconds, typed: raw }
}

/** The items as the server takes them. */
export function toSend(entries: readonly DraftEntry[]): PlaylistEntry[] {
  return entries.map(({ mediaItemId, seconds, fit }) => ({ mediaItemId, seconds, fit }))
}

/** The places of the items whose seconds the server would refuse. */
export function invalidItems(entries: readonly PlaylistEntry[], library: readonly SignageMedia[]): number[] {
  const kindOf = new Map(library.map((m) => [m.id, m.kind]))
  return entries.flatMap((e, i) => (secondsValid(e.seconds, kindOf.get(e.mediaItemId) ?? 'image') ? [] : [i]))
}

/** Where a playlist is used, as the card says it. */
export function usageOf(p: Pick<PlaylistSummary, 'campaigns' | 'designs'>): Said[] {
  const out: Said[] = []
  if (p.campaigns.length > 0) out.push({ key: 'signage.inCampaigns', n: p.campaigns.length })
  if (p.designs.length > 0) out.push({ key: 'signage.inDesigns', n: p.designs.length })
  return out.length > 0 ? out : [{ key: 'signage.notUsed' }]
}

/** The list as the TV player takes it; an item no longer in the library is left out, as the server does. */
export function playItems(entries: readonly PlaylistEntry[], library: readonly SignageMedia[]): MediaItem[] {
  const byId = new Map(library.map((m) => [m.id, m]))
  return entries.flatMap((e) => {
    const m = byId.get(e.mediaItemId)
    return m ? [{ assetId: m.assetId, kind: m.kind, seconds: e.seconds, fit: e.fit }] : []
  })
}

interface Playable {
  play: () => Promise<void> | void
  pause: () => void
}

/** Whether the reader asked for less motion. */
export function reducedMotion(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
}

/** Films that play while the pointer is on them, one at a time; none when less motion is asked for. */
export function hoverPlayer(reduced: () => boolean = reducedMotion) {
  let current: Playable | null = null
  return {
    enter(v: Playable) {
      if (current && current !== v) current.pause()
      current = null
      if (reduced()) return
      current = v
      // A browser may refuse to start a film; the poster stays.
      try {
        void Promise.resolve(v.play()).catch(() => undefined)
      } catch {
        // as above
      }
    },
    leave(v: Playable) {
      if (current !== v) return
      v.pause()
      current = null
    },
  }
}

export type StageShape = 'wide' | 'tall'

/** The screen the stage and Play draw on: a hall TV on its side, or standing. */
export function stageCanvas(shape: StageShape): { width: number; height: number } {
  return shape === 'tall' ? { width: 1080, height: 1920 } : { width: 1920, height: 1080 }
}

/** A centred item keeps its own pixels on the TV, so the stage shrinks it by the screen's scale. */
export function centreSize(natural: { width: number; height: number } | undefined, scale: number) {
  return natural ? { width: natural.width * scale, height: natural.height * scale } : undefined
}

/** The editor's running time, or null while an item's time is refused: a total then would count a guess. */
export function shownTotal(entries: readonly PlaylistEntry[], library: readonly SignageMedia[]): number | null {
  return invalidItems(entries, library).length > 0 ? null : totalSeconds(entries, library)
}

/** How long an item holds the screen, in seconds, as the timeline draws it: a film to its end its own length. */
export function itemSeconds(e: PlaylistEntry, m: Pick<SignageMedia, 'kind' | 'durationMs'> | undefined): number {
  if (e.seconds > 0) return e.seconds
  if (m?.kind === 'video') return Math.round((m.durationMs ?? 0) / 1000) || DEFAULT_PICTURE_SECONDS
  return DEFAULT_PICTURE_SECONDS
}

/** Each item's start and share of the whole, 0 to 1, for the timeline bar. */
export function timeline(entries: readonly PlaylistEntry[], library: readonly SignageMedia[]): Array<{ start: number; share: number; seconds: number }> {
  const byId = new Map(library.map((m) => [m.id, m]))
  const secs = entries.map((e) => itemSeconds(e, byId.get(e.mediaItemId)))
  const total = secs.reduce((a, b) => a + b, 0) || 1
  let at = 0
  return secs.map((seconds) => {
    const row = { start: at / total, share: seconds / total, seconds }
    at += seconds
    return row
  })
}

/** Library files put in at a place (the end when at is past it), as many as fit under the limit. */
export function insertEntries(entries: readonly DraftEntry[], chosen: readonly SignageMedia[], at: number): DraftEntry[] {
  const room = Math.max(0, MAX_ENTRIES - entries.length)
  const place = Math.min(Math.max(at, 0), entries.length)
  return [...entries.slice(0, place), ...chosen.slice(0, room).map(entryFor), ...entries.slice(place)]
}

/** What a save would send, as a string: the editor compares it to the saved one to say Unsaved. */
export function draftKey(name: string, entries: readonly DraftEntry[], ticker: readonly Record<string, string>[]): string {
  const line = (l: Record<string, string>) => Object.entries(l).filter(([, v]) => v !== '').sort(([a], [b]) => (a < b ? -1 : 1))
  return JSON.stringify([name.trim(), entries.map((e) => [e.mediaItemId, e.typed ?? e.seconds, e.fit]), ticker.map(line)])
}
